import { useEffect, useState } from "react";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import { AUTOPLAY_INTERVAL } from "@features/pulse/variant/others/utils/constants";

type UseGetAutoplayTimesProps = {
  /** Active article index in the carousel */
  activeArticle: number;
  /** Sorted pulse articles */
  sortedPulseArticles: SanitizedPulseData[];
  /** Function to navigate to the next article */
  handleNavigateRightCard: () => void;
  /** Flag to determine if is for autoplay version */
  isDisplayAutoplay: boolean;
};

/**
 * This hook manages the autoplay functionality for the pulse articles carousel.
 * It handles the autoplay timing, enabling/disabling autoplay, and pausing autoplay when the user is not interacting with the page.
 */
export const useGetAutoplayTimes = ({
  activeArticle,
  sortedPulseArticles,
  handleNavigateRightCard,
  isDisplayAutoplay,
}: UseGetAutoplayTimesProps) => {
  /** Autoplay is enabled by default. User can toggle it on/off */
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState<boolean>(true);
  /** Autoplay is paused by default for switching tabs or when the user is not interacting with*/
  const [isAutoplayPaused, setIsAutoplayPaused] = useState<boolean>(false);
  /** Autoplay left time in milliseconds
   * This is the time left for the autoplay to trigger the next article */
  const [autoplayLeft, setAutoplayLeft] = useState<number>(AUTOPLAY_INTERVAL);
  /** Autoplay total time in milliseconds
   * This is the total time that has passed since the autoplay started */
  const [autoplayTotal, setAutoplayTotal] = useState<number>(0);

  useEffect(() => {
    if (!isDisplayAutoplay) return;
    if (isAutoplayPaused) return;
    if (!isAutoplayEnabled) return;

    setAutoplayTotal(AUTOPLAY_INTERVAL - autoplayLeft);

    if (activeArticle === sortedPulseArticles.length + 1) return;

    const intervalId = setTimeout(() => {
      handleNavigateRightCard();
      resetAutoplayLeft();
    }, autoplayLeft);

    return () => {
      clearTimeout(intervalId);
    };
  }, [
    activeArticle,
    isAutoplayEnabled,
    isAutoplayPaused,
    autoplayLeft,
    handleNavigateRightCard,
    sortedPulseArticles,
    isDisplayAutoplay,
  ]);

  useEffect(() => {
    if (activeArticle === sortedPulseArticles.length + 1) return;

    if (!isDisplayAutoplay) return;
    if (isAutoplayPaused) return;
    if (!isAutoplayEnabled) return;

    if (
      activeArticle === sortedPulseArticles.length &&
      autoplayTotal === AUTOPLAY_INTERVAL
    )
      return;

    const autoplayLeftInterval = setInterval(() => {
      setAutoplayTotal((prevCount) => prevCount + 1000);
    }, 1000);

    return () => {
      clearInterval(autoplayLeftInterval);
    };
  }, [
    activeArticle,
    sortedPulseArticles,
    autoplayTotal,
    isAutoplayEnabled,
    isAutoplayPaused,
    isDisplayAutoplay,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAutoplayEnabled) return;
    if (!isDisplayAutoplay) return;

    const handleVisibilityChange = () => {
      if (!window.document.hidden) {
        setIsAutoplayPaused(false);
        return;
      }

      setIsAutoplayPaused(true);
      const left = AUTOPLAY_INTERVAL - autoplayTotal;
      setAutoplayLeft(left);
    };

    window.document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      window.document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [autoplayTotal, isAutoplayEnabled, isDisplayAutoplay]);

  /** handle to paused the autoplay when social popover open */
  const handleSetIsAutoplayPaused = (isEnabled: boolean) => {
    setIsAutoplayPaused(isEnabled);

    const left = AUTOPLAY_INTERVAL - autoplayTotal;
    setAutoplayLeft(left);
  };

  const resetAutoplayLeft = () => {
    setAutoplayTotal(0);
    setAutoplayLeft(AUTOPLAY_INTERVAL);
  };

  return {
    autoplayTotal,
    isAutoplayEnabled,
    setIsAutoplayEnabled,
    autoplayLeft,
    setAutoplayLeft,
    handleSetIsAutoplayPaused,
    resetAutoplayLeft,
  };
};
